/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import { AccountCircle } from '@mui/icons-material';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { ListItemIcon, ListItemText } from '@mui/material';
import ButtonBase from '@mui/material/ButtonBase';

import { BasicRoutes } from '../../constants/basicRoutes';
import { Namespaces } from '../../i18n';
import { userLogout } from '../../store/auth/actions';
import { IUser } from '../../types/user/IUser';
import { getUserThumbnailVersion } from '../../utils/getUserThumbnailVersion';
import { EntityThumbnail } from '../EntityThumbnail/EntityThumbnail';
import { ArrowRoundedIcon } from '../svg-icons/ArrowRoundedIcon';
import { createProfileDropdownStyles, StyledMenu, StyledMenuItem } from './ProfileDropdown.styles';

type ProfileDropdownProps = {
  name: string;
  user?: IUser;
};

export const ProfileDropdown = memo<ProfileDropdownProps>(props => {
  const { name, user } = props;
  const navigate = useNavigate();

  const classes = createProfileDropdownStyles();
  const { t } = useTranslation(Namespaces.Navigation);
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const logoutUserAction = () => {
    dispatch(userLogout());
  };

  const goToProfile = () => {
    handleClose();
    void navigate(BasicRoutes.PROFILE);
  };

  const thumbnailVersion = useMemo(() => getUserThumbnailVersion(user), [user]);

  return (
    <>
      <ButtonBase
        className={classes.root}
        aria-label="Account of current user"
        aria-controls="customized-menu"
        aria-haspopup="true"
        onClick={handleClick}
        data-cy="profile-dropdown"
      >
        <EntityThumbnail
          size={40}
          type="user"
          imageClassName={classes.avatar}
          mergeVersion={true}
          version={thumbnailVersion}
        />
        <div className={classes.name}>{name}</div>
        <ArrowRoundedIcon
          className={cx(classes.icon, {
            [classes.iconOpen]: Boolean(anchorEl),
          })}
        />
      </ButtonBase>

      <StyledMenu id="customized-menu" anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleClose}>
        <StyledMenuItem onClick={() => goToProfile()} data-cy="profile-dropdown-my-profile">
          <ListItemIcon>
            <AccountCircle />
          </ListItemIcon>
          <ListItemText primary={t('profile.my_profile')} />
        </StyledMenuItem>

        <StyledMenuItem onClick={logoutUserAction} data-cy="profile-dropdown-logout">
          <ListItemIcon>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText primary={t('profile.logout')} />
        </StyledMenuItem>
      </StyledMenu>
    </>
  );
});
